/// <amd-module name="Build/PipelineInsights/RunsInsights/ControllerViews/RunsInsights" />
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { ContextType, IReportContext, ITrendData, ITrendReportConfiguration, Period, TrendBy, TrendType } from "Analytics/Shared/Common/Types";
import { getTrendFromData } from "Analytics/Shared/Common/Utils/TrendCalculator";
import { PipelineInsightsClientSettings, serviceInstanceId, Telemetry } from "Build/PipelineInsights/RunsInsights/Constants";
import * as Resources from "Build/PipelineInsights/RunsInsights/Resources";
import * as React from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import { localeFormat } from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { TeamFoundationHostType } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { Icon } from "VSSUI/Icon";
import { css } from "VSSUI/Util";
import { RunsInsightsSource } from "./../Sources/RunsInsightsSource";
import { PipelineInsightsDialog } from "Pipeline/Analytics/PipelineInsights/ControllerView/PipelineInsightsDialog";
import { onClickFPS } from "VSS/Platform/FPS";
import { Observer } from "VSSUI/Observer";
import { ObservableValue } from "VSS/Core/Observable";
import { ISettingsRestClient } from "VSS/Platform/RestClient/Settings";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import * as DateUtil from "Analytics/Shared/Utils/DateUtils";

export interface IRunsInsightsProps extends IVssComponentProperties {
    definitionId: number;
}

export interface IRunsInsightsState {
    definitionId: number;
    passPercentage: number | undefined;
    passRateTrend: ITrendData | undefined;
    errorText?: string;
}

export class RunsInsights extends VssComponent<IRunsInsightsProps, IRunsInsightsState> {
    private _pipelineReportRouteId = "ms.vss-pipelineanalytics-web.pipeline-outcome-route";
    private _runsInsightsSource: RunsInsightsSource;
    private _telemetryService: IVssTelemetryService;
    private _showInsights = new ObservableValue<boolean>(false);
    private PipelineInsightsTimeOffsetInSeconds = 90 * 24 * 60 * 60 * 60;

    constructor(props: IRunsInsightsProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            definitionId: this.props.definitionId
        } as Readonly<IRunsInsightsState>;

        if (isFeatureFlagEnabled(context.pageContext, PipelineEntities.RenameBuildEntitiesFeatureFlag)) {
            PipelineEntities.switchEntityAndAttributeNames();
        }

        this._runsInsightsSource = new RunsInsightsSource(this.context.pageContext);
        this._telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    public render() {
        if (!isFeatureFlagEnabled(this.context.pageContext, "WebAccess.Pipeline.Analytics.PipelineInsightsPopup", false)) {
            return null;
        }

        if (this.state.errorText) {
            this._telemetryService.publishEvent(Telemetry.Area, Telemetry.RunsInsightsOnError, {
                PipelineId: this.state.definitionId,
                error: this.state.errorText
            });
            return null;
        }

        return this.areInsightsValuable() ? (
            <div className="runs-incontext-view">
                <Observer showInsights={this._showInsights}>
                    {(props: { showInsights: boolean }) => {
                        return props.showInsights ? (
                            <PipelineInsightsDialog
                                insightsMessage={this.getInsights()}
                                cardTitle={Resources.DialogTitle}
                                onDismiss={this.onDismiss}
                                onViewReportClick={this.onClick}
                            />
                        ) : null;
                    }}
                </Observer>
            </div>
        ) : null;
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (!this.state.errorText && this.state.definitionId) {
            this.fetchData();
        }
        this.evaluateShowInsights();
    }

    public componentDidUpdate(prevProps: IRunsInsightsProps, prevState: IRunsInsightsState): void {
        if (this.props.definitionId && this.props.definitionId !== this.state.definitionId) {
            this.setState(
                {
                    definitionId: this.props.definitionId,
                    passPercentage: undefined,
                    passRateTrend: undefined
                },
                () => {
                    this.fetchData();
                }
            );
        }
        super.componentDidUpdate(prevProps, prevState);
    }

    private getTrendElement(passRateTrend: ITrendData): JSX.Element {
        return (
            <Icon
                className={css(
                    "font-size-xs font-weight-semibold",
                    passRateTrend.trendType === TrendType.Increased ? "positive-color" : "negative-color"
                )}
                iconName={passRateTrend.trendType === TrendType.Increased ? "TriangleSolidUp12" : "TriangleSolidDown12"}
            />
        );
    }

    private getInsights(): JSX.Element {
        this.publishTelemetry(Telemetry.Area, Telemetry.RunsInsightsTriggerData);
        if (this.state.passRateTrend) {
            const passRateTrend = this.state.passRateTrend;
            const trend = passRateTrend.trendType === TrendType.Increased ? "increased" : "decreased";
            const arialabel = localeFormat(Resources.InContextTrendText, this.state.passPercentage, trend, passRateTrend.percentageVariation);
            return (
                <div aria-label={arialabel}>
                    <FormatComponent format={Resources.InContextTrendText}>
                        {this.state.passPercentage}
                        {this.getTrendElement(passRateTrend)}
                        {passRateTrend.percentageVariation}
                    </FormatComponent>
                </div>
            );
        } else {
            return (
                <div tabIndex={0}>
                    <FormatComponent format={Resources.InContextText}>{this.state.passPercentage}</FormatComponent>
                </div>
            );
        }
    }

    private areInsightsValuable(): boolean {
        const passRateTrend = this.state.passRateTrend;
        if (this.state.passPercentage != null) {
            if (passRateTrend != null && passRateTrend.trendType == TrendType.Decreased && passRateTrend.percentageVariation >= 5) {
                return true;
            }
        }

        return false;
    }

    private async fetchData() {
        const reportContext = {
            contextType: ContextType.Build,
            definitionId: this.state.definitionId
        } as IReportContext;

        const defaultConfValues = {
            trendBy: TrendBy.Days,
            period: Period.Days_14
        } as ITrendReportConfiguration;

        try {
            const vssLocationService: IVssLocationService = this.context.pageContext.getService<IVssLocationService>("IVssLocationService");
            const axServicePromise = await vssLocationService.getServiceLocationAsync(serviceInstanceId, TeamFoundationHostType.ProjectCollection);
            const runsInsightsData = await this._runsInsightsSource.queryPipelineInsightsData(reportContext, defaultConfValues);
            this.setState({
                passPercentage: runsInsightsData.passPercentage,
                passRateTrend: getTrendFromData(runsInsightsData.initialValue, runsInsightsData.finalValue, false)
            });
        } catch (error) {
            this.onError(error);
        }
    }

    private getPipelineReportPageRoute() {
        const tfsProjectData = getTFSPageData(this.context.pageContext);

        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = tfsProjectData && tfsProjectData.project ? tfsProjectData.project.name : "";
        routeValues["definitionId"] = this.state.definitionId ? this.state.definitionId.toString() : "";

        return routeUrl(this.context.pageContext, this._pipelineReportRouteId, routeValues);
    }

    private onError(error: any) {
        const errorText: string = error.toString();
        this.setState({ errorText: errorText });
    }

    private onClick = (): void => {
        onClickFPS(this.context.pageContext, this.getPipelineReportPageRoute(), false);
        this.publishTelemetry(Telemetry.Area, Telemetry.RunsInsightsViewReport);
    };

    private publishTelemetry(area: string, feature: string): void {
        const passRateTrend = this.state.passRateTrend;
        this._telemetryService.publishEvent(area, feature, {
            PipelineId: this.state.definitionId,
            PassPercentage: this.state.passPercentage,
            PercentageVariation: passRateTrend ? passRateTrend.percentageVariation : "",
            Trend: passRateTrend ? (passRateTrend.trendType === TrendType.Increased ? "increased" : "decreased") : ""
        });
    }

    private onDismiss = () => {
        this.props.onDismiss;
        this._showInsights.value = false;
        if (this.props.definitionId != null) {
            const settingEntries: { [key: string]: number } = {};
            const date = DateUtil.shiftToUTC(DateUtil.getDate()).getTime();
            settingEntries[PipelineInsightsClientSettings + "/" + this.props.definitionId + "/dismissed"] = date;
            const settingsClient = this.context.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
            settingsClient.setEntries(settingEntries, "me");
        }
        this.publishTelemetry(Telemetry.Area, Telemetry.RunsInsightsOnDismiss);
    };

    private async evaluateShowInsights(): Promise<void> {
        const settingsClient = this.context.pageContext.getRestClient<ISettingsRestClient>("ISettingsRestClient");
        const viewInsightsSettings = userHasClaim(this.context.pageContext, UserClaims.Member)
            && await settingsClient.getEntries("me", PipelineInsightsClientSettings + "/" + this.props.definitionId);
        
        if (viewInsightsSettings && viewInsightsSettings["dismissed"]) {
            this._showInsights.value = this.hasUserDismissalPeriodExpired(viewInsightsSettings["dismissed"]);
        } else {
            this._showInsights.value = true;
        }
    }

    private hasUserDismissalPeriodExpired(expirationTimeInDays: number) {
        return DateUtil.shiftToUTC(DateUtil.getDate()).getTime() - expirationTimeInDays >= this.PipelineInsightsTimeOffsetInSeconds ? true : false;
    }
}

Components.add("pipelines-runs-insights", RunsInsights);
